'use client';

import { FunctionComponent } from 'react';

import { ContentType } from '@/services/isomorphic/ContentService';
import { Button } from '@/react/components/core-ui/Button';
import { FragmentParser } from '@/react/components/utility/FragmentParser';

import { useModal } from '@/react/hooks/useModal';
import type { ButtonVariant } from '@/react/components/core-ui/Button/IBaseButtonProps';
import { useIsMounted } from '@/react/components/core-ui/hooks/useIsMounted';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { Spinner } from '@/react/components/core-ui/Spinner';
import { ModalLinkModal } from './internal/ModalLinkModal';
import { FetchContentWhenActive } from '../FetchContentWhenActive';

import S from './styles.base.module.scss';

/**
 * The props for the ModalLink component.
 */
export interface IModalLinkProps {
  /**
   * The modal link in the content.
   */
  text?: string;

  /**
   * The title of the modal.
   */
  modalTitle?: string;

  /**
   * The content ID of the modal.
   */
  contentID?: string;

  /** Adds a class to the container. */
  className?: string;

  /** Button variant to pass on to the button. */
  buttonVariant?: ButtonVariant;

  /**
   * The content type of the modal.
   */
  contentType?: ContentType.Fragment | ContentType.Page;
}

/**
 * All this does is apply custom styling to the component within it.
 */
export const ModalLink: FunctionComponent<IModalLinkProps> = ({
  text = '',
  modalTitle = '',
  contentID = '',
  className,
  buttonVariant = 'link',
  contentType = ContentType.Fragment
}) => {
  const isMounted = useIsMounted();

  // Leverage the useModal() hook to show / hide the modal.
  const {
    isOpen: isModalOpen,
    open: handleModalOpen,
    close: handleModalClose
  } = useModal();

  return (
    <div className={classes(S.contentWrapper, className ?? '')}>
      <Button
        disabled={!isMounted}
        onClick={handleModalOpen}
        variant={buttonVariant}
      >
        <FragmentParser string={text ?? ''} />
      </Button>
      {isMounted && (
        <ModalLinkModal
          modalTitle={modalTitle}
          isOpen={isModalOpen}
          handleModalClose={handleModalClose}
        >
          <FetchContentWhenActive
            options={{ id: contentID, type: contentType }}
            isActive={isModalOpen}
            fallback={<Spinner />}
            handleLinkClick={handleModalClose}
          />
        </ModalLinkModal>
      )}
    </div>
  );
};

export default ModalLink;
